import { openSupportChat } from "../../services/supportChat";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { loadStripe, Stripe as StripeClient } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useSetRecoilState } from "recoil";
import { styled } from "@mui/system";
import { toast } from "react-toastify";

import Dialog from "../Display/Dialog";
import Button, { ButtonVariants } from "../Display/Button";
import Text, { TextVariant } from "../Display/Text";
import Loading, { LoadingVariant } from "../Display/Loading";
import FormHelper, { Severity } from "../DataInput/FormHelper";
import {
  COLORS,
  FONT_FAMILIES,
  FONT_WEIGHTS,
  GAPS,
  RADII,
  SPACINGS,
} from "../../common/consts";
import { useTheme } from "../../common/contexts/ThemeContext";
import { makeShouldForwardProps } from "../../common/utils/ui";
import { organizationAtom } from "../../state/atoms";
import {
  createBillingSetupIntent,
  getBillingStatus,
  getMyOrganization,
  setupProspectWebhook,
  startBillingSubscription,
} from "../../services/firebase";
import { BillingStatus } from "../../types/billing";
import { BillingInterval, BillingPlanKey } from "../../types/organizations";
import { PLAN_CATALOGUE, checkoutPlans, isPackagePlan, planIncludesDialer } from "../../common/plans";
import Checkbox from "../DataInput/Checkbox";
import { buildStripeAppearance } from "./stripeAppearance";

const IntervalToggle = styled("div")`
  display: flex;
  gap: ${SPACINGS.xs};
  padding: ${SPACINGS.xs};
  border-radius: ${RADII.md};
  background-color: var(--color-backgroundSecondary, ${COLORS.backgroundSecondary});
  width: fit-content;
`;

interface IntervalOptionProps {
  active?: boolean;
}
const intervalShouldForwardProp = makeShouldForwardProps<IntervalOptionProps>([
  "active",
]);
const IntervalOption = styled("button", {
  shouldForwardProp: intervalShouldForwardProp,
})<IntervalOptionProps>`
  border: none;
  cursor: pointer;
  padding: ${SPACINGS.xs} ${SPACINGS.md};
  border-radius: ${RADII.sm};
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.875rem;
  font-weight: ${FONT_WEIGHTS.medium};
  background-color: ${({ active }) =>
    active
      ? `var(--color-backgroundWhite, ${COLORS.backgroundWhite})`
      : "transparent"};
  color: ${({ active }) =>
    active
      ? `var(--color-foregroundBrand, ${COLORS.foregroundBrand})`
      : `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})`};
  box-shadow: ${({ active }) => (active ? "0 1px 2px rgba(0,0,0,0.08)" : "none")};
  transition: all 0.15s ease;
`;

const PlanGrid = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: ${GAPS.sm};
  align-items: stretch;

  & > * {
    min-width: 150px;
  }
`;

interface PlanCardProps {
  active?: boolean;
}
const planCardShouldForwardProp = makeShouldForwardProps<PlanCardProps>([
  "active",
]);
const PlanCard = styled("button", {
  shouldForwardProp: planCardShouldForwardProp,
})<PlanCardProps>`
  flex: 1 1 0;
  cursor: pointer;
  text-align: left;
  padding: ${SPACINGS.md};
  border-radius: ${RADII.md};
  border: 1px solid
    ${({ active }) =>
      active
        ? `var(--color-foregroundBrand, ${COLORS.foregroundBrand})`
        : `var(--color-borderPrimary, ${COLORS.borderPrimary})`};
  box-shadow: ${({ active }) =>
    active
      ? `0 0 0 1px var(--color-foregroundBrand, ${COLORS.foregroundBrand})`
      : "none"};
  background-color: var(--color-backgroundSecondaryCard, ${COLORS.backgroundSecondaryCard});
  display: flex;
  flex-direction: column;
  gap: ${GAPS.xs};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
`;

const PlanFeatureList = styled("ul")`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PlanFeature = styled("li")`
  font-family: ${FONT_FAMILIES.regular};
  font-size: 0.75rem;
  line-height: 1.5;
  color: var(--color-foregroundSecondary, ${COLORS.foregroundSecondary});
  display: flex;
  gap: 6px;
  align-items: baseline;

  &::before {
    content: "✓";
    color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
    font-weight: ${FONT_WEIGHTS.semibold};
  }
`;

const ContactSalesButton = styled("button")`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-family: ${FONT_FAMILIES.regular};
  font-size: inherit;
  color: var(--color-foregroundBrand, ${COLORS.foregroundBrand});
  text-decoration: underline;
`;

/**
 * Checkout-facing plan copy: what checkout sells (the backend's sellablePlans:
 * the packages, or the legacy plans until the package prices are configured)
 * and what each includes (common/plans.ts, the table the backend gates on).
 * Prices come from Stripe via planPrices; a plan without them is not shown.
 */
const planOptionsFor = (status: BillingStatus | null): { key: BillingPlanKey; name: string; features: string[] }[] =>
  checkoutPlans(status?.sellablePlans)
    .filter((key) => status?.config.planPrices?.[key])
    .map((key) => ({ key, name: PLAN_CATALOGUE[key].name, features: PLAN_CATALOGUE[key].includes }));

/* Sales questions go to the support chat (Everything's; services/supportChat). */
const openSalesChat = () => openSupportChat();

const SummaryCard = styled("div")`
  border: 1px solid var(--color-borderPrimary, ${COLORS.borderPrimary});
  border-radius: ${RADII.md};
  padding: ${SPACINGS.md} ${SPACINGS.lg};
  display: flex;
  flex-direction: column;
  gap: ${GAPS.xs};
  background-color: var(--color-backgroundSecondaryCard, ${COLORS.backgroundSecondaryCard});
`;

const SummaryRow = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: ${GAPS.md};
`;

const formatAmount = (amountInCents: number, currency: string): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: amountInCents % 100 === 0 ? 0 : 2,
  }).format(amountInCents / 100);
};

interface CheckoutFormProps {
  interval: BillingInterval;
  plan: BillingPlanKey;
  addDialFaster: boolean;
  isTrial: boolean;
  onDone: () => Promise<void>;
}

const CheckoutForm: FC<CheckoutFormProps> = ({
  interval,
  plan,
  addDialFaster,
  isTrial,
  onDone,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) {
      return;
    }
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || "Please check your card details");
        return;
      }

      const { error: setupError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });
      if (setupError) {
        setErrorMessage(setupError.message || "Could not save your card");
        return;
      }

      const paymentMethodId =
        typeof setupIntent?.payment_method === "string"
          ? setupIntent.payment_method
          : setupIntent?.payment_method?.id;
      if (!paymentMethodId) {
        setErrorMessage("Could not save your card. Please try again.");
        return;
      }

      const result = await startBillingSubscription({
        interval,
        paymentMethodId,
        plan,
        ...(addDialFaster ? { addDialFaster: true } : {}),
      });

      // Paid (non-trial) subscriptions have a first invoice to confirm (3DS).
      if (result.clientSecret) {
        const { error: paymentError } = await stripe.confirmPayment({
          clientSecret: result.clientSecret,
          confirmParams: {},
          redirect: "if_required",
        });
        if (paymentError) {
          setErrorMessage(
            paymentError.message ||
              "Your card was saved but the payment failed. Please try again."
          );
          return;
        }
      }

      await onDone();
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Something went wrong"
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [stripe, elements, interval, plan, addDialFaster, onDone]);

  return (
    <div className="d-flex flex-column" style={{ gap: GAPS.md }}>
      <PaymentElement options={{ layout: "tabs" }} />
      <FormHelper helperText={errorMessage} severity={Severity.error} />
      <Button
        fullWidth
        variant={ButtonVariants.brand}
        onClick={handleSubmit}
        disabled={!stripe || isSubmitting}
        loading={isSubmitting}
        type="button"
      >
        {isTrial ? "Start free trial" : "Subscribe"}
      </Button>
    </div>
  );
};

export interface CheckoutDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after the subscription is live and the org atom is refreshed. */
  onSuccess?: () => void;
}

const CheckoutDialog: FC<CheckoutDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { mode } = useTheme();
  const setOrganization = useSetRecoilState(organizationAtom);
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] =
    useState<Promise<StripeClient | null> | null>(null);
  const [interval, setBillingInterval] = useState<BillingInterval>("monthly");
  const [plan, setPlan] = useState<BillingPlanKey | null>(null);
  const [addDialFaster, setAddDialFaster] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }
    let isMounted = true;
    setLoadError("");
    setClientSecret(null);
    (async () => {
      try {
        const [billingStatus, setupIntent] = await Promise.all([
          getBillingStatus(),
          createBillingSetupIntent(),
        ]);
        if (!isMounted) {
          return;
        }
        setStatus(billingStatus);
        // A new organization starting its free trial (which includes
        // everything) is offered the bundle first; anyone else the plan they
        // are on when checkout still sells it, else the bundle.
        const forSale = planOptionsFor(billingStatus).map((option) => option.key);
        const bundle = forSale.find((key) => key === "everything" || key === "prospector");
        const newTrial = billingStatus.trialEligible && billingStatus.planGating?.gated;
        setPlan(
          (!newTrial && forSale.includes(billingStatus.plan) ? billingStatus.plan : bundle) ||
            forSale[0] ||
            billingStatus.plan
        );
        setAddDialFaster(false);
        setClientSecret(setupIntent.clientSecret);
        setStripePromise((existing) =>
          existing || loadStripe(setupIntent.publishableKey)
        );
      } catch (error) {
        if (isMounted) {
          setLoadError(
            error instanceof Error
              ? error.message
              : "Could not load the payment form"
          );
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [open]);

  const isTrial = !!status?.trialEligible;

  const seatsBilled = useMemo(() => {
    if (!status) {
      return 1;
    }
    const seats = Math.max(1, status.seatsAssigned);
    return isTrial ? Math.min(seats, status.trial.maxUsers) : seats;
  }, [status, isTrial]);

  const selectedPlan: BillingPlanKey = plan || status?.plan || "verify";

  // The plans for sale whose Stripe prices have loaded.
  const planOptions = useMemo(() => planOptionsFor(status), [status]);
  const showPlanPicker = planOptions.length > 0;

  const priceSet =
    status?.config.planPrices?.[selectedPlan] || status?.config.prices || null;
  const basePrice = interval === "yearly" ? priceSet?.yearly : priceSet?.monthly;

  // Dial faster as the add-on on a package that leaves it out.
  const dialFasterPriceSet = status?.config.planPrices?.dialFaster || null;
  const dialFasterPrice = interval === "yearly" ? dialFasterPriceSet?.yearly : dialFasterPriceSet?.monthly;
  const canAddDialFaster = isPackagePlan(selectedPlan) && !planIncludesDialer(selectedPlan) && !!dialFasterPrice?.unitAmount;
  const withDialFaster = addDialFaster && canAddDialFaster;
  const price =
    basePrice && withDialFaster && typeof basePrice.unitAmount === "number" && typeof dialFasterPrice?.unitAmount === "number"
      ? { ...basePrice, unitAmount: basePrice.unitAmount + dialFasterPrice.unitAmount }
      : basePrice;

  const handleDone = useCallback(async () => {
    // The callable mirrors state synchronously, but refresh until the org doc
    // reflects the subscription so the whole app picks it up.
    for (let attempt = 0; attempt < 8; attempt++) {
      try {
        const { result } = await getMyOrganization();
        if (result) {
          setOrganization(result);
          const billingStatus = result.billing?.status;
          if (billingStatus === "trialing" || billingStatus === "active") {
            break;
          }
        }
      } catch {
        // Keep polling; a transient failure here shouldn't fail checkout.
      }
      await new Promise((resolve) => setTimeout(resolve, 800));
    }

    // The prospect webhook is only registered for paying/trialing orgs, so
    // (re)try now that the subscription is live.
    try {
      await setupProspectWebhook();
    } catch {
      // Best-effort: org may not have connected an integration yet.
    }

    toast.success(
      isTrial ? "Your free trial has started!" : "You're subscribed!"
    );
    onClose();
    onSuccess?.();
  }, [isTrial, onClose, onSuccess, setOrganization]);

  const appearance = useMemo(() => buildStripeAppearance(mode), [mode]);

  const renderBody = () => {
    if (loadError) {
      return <FormHelper helperText={loadError} severity={Severity.error} />;
    }
    if (!status || !clientSecret || !stripePromise) {
      return (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: 220 }}
        >
          <Loading variant={LoadingVariant.CIRCULAR} />
        </div>
      );
    }

    const perSeatLabel = price?.unitAmount
      ? `${formatAmount(price.unitAmount, price.currency)} / user / ${
          interval === "yearly" ? "year" : "month"
        }`
      : null;
    const totalLabel = price?.unitAmount
      ? formatAmount(price.unitAmount * seatsBilled, price.currency)
      : null;

    return (
      <div className="d-flex flex-column" style={{ gap: GAPS.md }}>
        <IntervalToggle>
          <IntervalOption
            type="button"
            active={interval === "monthly"}
            onClick={() => setBillingInterval("monthly")}
          >
            Monthly
          </IntervalOption>
          <IntervalOption
            type="button"
            active={interval === "yearly"}
            onClick={() => setBillingInterval("yearly")}
          >
            Yearly
          </IntervalOption>
        </IntervalToggle>

        {showPlanPicker && (
          <PlanGrid>
            {planOptions.map((option) => {
              const optionPriceSet = status.config.planPrices?.[option.key];
              const optionPrice =
                interval === "yearly"
                  ? optionPriceSet?.yearly
                  : optionPriceSet?.monthly;
              return (
                <PlanCard
                  key={option.key}
                  type="button"
                  active={selectedPlan === option.key}
                  onClick={() => setPlan(option.key)}
                >
                  <Text
                    variant={TextVariant.sm}
                    style={{ fontWeight: FONT_WEIGHTS.semibold }}
                  >
                    {option.name}
                  </Text>
                  {optionPrice?.unitAmount && (
                    <Text variant={TextVariant.sm}>
                      {formatAmount(optionPrice.unitAmount, optionPrice.currency)}
                      <span
                        style={{
                          color: `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})`,
                        }}
                      >
                        {" "}
                        / user / {interval === "yearly" ? "year" : "month"}
                      </span>
                    </Text>
                  )}
                  <PlanFeatureList>
                    {option.features.map((feature) => (
                      <PlanFeature key={feature}>{feature}</PlanFeature>
                    ))}
                  </PlanFeatureList>
                </PlanCard>
              );
            })}
          </PlanGrid>
        )}

        {canAddDialFaster && dialFasterPrice?.unitAmount && (
          <label className="d-flex align-items-center" style={{ gap: GAPS.sm, cursor: "pointer" }}>
            <Checkbox checked={addDialFaster} onChange={setAddDialFaster} aria-label="Add Dial faster" />
            <Text variant={TextVariant.sm}>
              Add Dial faster, the power dialer, for {formatAmount(dialFasterPrice.unitAmount, dialFasterPrice.currency)} / user /{" "}
              {interval === "yearly" ? "year" : "month"} (included in Everything)
            </Text>
          </label>
        )}

        <SummaryCard>
          <SummaryRow>
            <Text variant={TextVariant.sm}>
              {seatsBilled} license{seatsBilled === 1 ? "" : "s"}
              {perSeatLabel ? ` · ${perSeatLabel}` : ""}
            </Text>
            {totalLabel && (
              <Text
                variant={TextVariant.md}
                style={{ fontWeight: FONT_WEIGHTS.semibold }}
              >
                {totalLabel}
                <span style={{ fontWeight: FONT_WEIGHTS.regular }}>
                  {" "}
                  / {interval === "yearly" ? "year" : "month"}
                </span>
              </Text>
            )}
          </SummaryRow>
          {isTrial ? (
            <Text
              variant={TextVariant.sm}
              style={{
                color: `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})`,
              }}
            >
              Free for {status.trial.durationDays} days — up to{" "}
              {status.trial.maxUsers} users,{" "}
              {status.trial.verificationsPerUser.toLocaleString()} verifications
              per user. You won&apos;t be charged until the trial ends, and you
              can cancel anytime.
            </Text>
          ) : (
            <Text
              variant={TextVariant.sm}
              style={{
                color: `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})`,
              }}
            >
              Licenses are billed per user. Add or remove licenses anytime —
              your subscription adjusts automatically.
            </Text>
          )}
        </SummaryCard>

        <Elements
          stripe={stripePromise}
          options={{ clientSecret, appearance }}
        >
          <CheckoutForm
            interval={interval}
            plan={selectedPlan}
            addDialFaster={withDialFaster}
            isTrial={isTrial}
            onDone={handleDone}
          />
        </Elements>

        <Text
          variant={TextVariant.sm}
          style={{
            textAlign: "center",
            color: `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})`,
          }}
        >
          Need more seats, a custom quote, or invoice billing?{" "}
          <ContactSalesButton type="button" onClick={openSalesChat}>
            Talk to us
          </ContactSalesButton>
        </Text>
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isTrial ? "Start your free trial" : "Subscribe"}
      maxWidth={560}
    >
      {renderBody()}
    </Dialog>
  );
};

export default CheckoutDialog;
